import { useNavigate } from '@tanstack/react-router';
import { ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useCanManageTransformers } from '~/hooks/useCanManageTransformers';
import { useSwitchToSystemTenant } from '~/hooks/useSwitchToSystemTenant';
import { useAuthStore } from '~/store/auth.store';

import type { FileLayoutState } from '../../hooks/use-file-layout';

/**
 * Stands in for the Export sheet's template preview when a file transformer builds
 * the file: what the file will be called, a one-line note, and (super admins) a
 * deep link to that transformer's editor, switching to Lily AI first if needed.
 */
export function LayoutExportNote({
  layout,
  testId = 'layout-export-note',
}: {
  layout: FileLayoutState;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}) {
  const { t } = useTranslation('transformers');
  const navigate = useNavigate();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin === true);
  const inSystemTenant = useCanManageTransformers();
  const { switchToSystem, isSwitching, canSwitch } = useSwitchToSystemTenant();
  const transformerId = layout.transformerId;

  return (
    <div
      data-testid={testId}
      className="bg-surface-blue flex flex-col gap-2 rounded-lg border px-4 py-3 text-sm"
    >
      <LayoutFileNames testId={`${testId}-file-names`} layout={layout} />
      <p data-testid={`${testId}-note`} className="text-muted-foreground text-xs">
        {t('layout.previewNote')}
      </p>
      {isSuperAdmin && transformerId && (
        <Button
          data-testid={`${testId}-open-editor-button`}
          type="button"
          variant="link"
          size="sm"
          className="h-auto w-fit cursor-pointer p-0 disabled:cursor-not-allowed"
          isLoading={isSwitching}
          disabled={!inSystemTenant && !canSwitch}
          onClick={() => {
            if (inSystemTenant) {
              void navigate({
                to: '/platform/transformers/$transformerId',
                params: { transformerId },
              });
              return;
            }
            void switchToSystem(`/platform/transformers/${encodeURIComponent(transformerId)}`);
          }}
        >
          <ExternalLink aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
          {t('layout.openEditor')}
        </Button>
      )}
    </div>
  );
}

/** "File: lilyai_….tsv" — the name(s) the layout in force writes, from resolve. */
export function LayoutFileNames({
  layout,
  testId = 'layout-file-names',
}: {
  layout: FileLayoutState;
  /** E2E `data-testid` root; each name is `${testId}-item-<name>`. */
  testId?: string;
}) {
  const { t } = useTranslation('transformers');
  if (layout.fileNamesLoading) return <Skeleton className="h-5 w-64" />;
  if (layout.fileNames.length === 0) return null;
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5">
      {layout.fileNames.map((name) => (
        <span
          key={name}
          data-testid={`${testId}-item-${name}`}
          className="flex items-center gap-1.5 text-xs"
        >
          <FileText aria-hidden="true" className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
          <span className="text-muted-foreground">{t('layout.file')}</span>
          <span className="font-mono break-all">{name}</span>
        </span>
      ))}
    </div>
  );
}
